/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Pressable } from '@components/atoms'
import { Footer, Seo } from '@components/systems'
import { buildProductJsonLd, buildBreadcrumbJsonLd } from '@utils'
import { SEO_SITE, getSeoEntry } from '@src/configs/seo.config'

import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '@redux/index'
import { SeasonProduct, SeasonProductType } from '@schemas'

/**
 * Koinos PLS 8 랜딩.
 *
 * 글의 흐름과 카피는 콘티(8 beats)를 그대로 따른다. 근거 문서:
 * - `claudeFiles/kedem8/manual/koinos8-manual-en.md` — "Why This Exists"
 * - `claudeFiles/kedem8/KOINOS8_인트로영상_스크립트.md` — 어필 순위, 주장 금지 목록
 *
 * 톤 규칙 세 가지:
 * 1. 최상급을 쓰지 않는다. 사실을 놓고 멈춘다. 여백이 대신 말한다.
 * 2. 경쟁사는 계보 문단에서 한 번, 존중으로만. 비교표는 두지 않는다.
 * 3. 루프 스위처가 무엇인지 설명하지 않는다. 아는 사람을 위해 쓴다.
 *
 * 주장 금지(영상 스크립트): 병렬 라우팅/내부 믹서, 듀얼앰프 아이솔레이션,
 * Stomp 모드, AirMIDI, 기기 내 펌웨어 업데이트 화면.
 *
 * TODO: 제품 사진이 나오면 hero 와 §turn 에 이미지를 넣고 ShopOutline 으로 구매 UI 를 붙인다.
 *       지금은 가격이 미정(seasonUsd = 0)이라 텍스트만으로 구성한다.
 */
const ShopKoinosPls8Page = () => {
	const navigate = useNavigate()

	const seasonProductList: SeasonProductType[] = useAppSelector(
		state => state.shop.productList,
	)

	// DB 의 productCode 는 소문자 `pls8` 로 등록돼 있다. 다른 제품(KDM6, AMP2 …)은
	// 대문자라 나중에 통일될 수 있으므로 대소문자를 무시하고 찾는다.
	const thisProduct: SeasonProductType = React.useMemo(() => {
		const filterRes = seasonProductList.filter(
			aProduct => (aProduct.productCode ?? '').toUpperCase() === 'PLS8',
		)

		if (filterRes.length === 0) {
			return new SeasonProduct()
		}

		return filterRes[0]
	}, [seasonProductList])

	// 가격이 잡히기 전에는 PreOrder 로 알린다. 시즌에 가격이 등록되면 실제 Offer 로 바뀐다.
	const isPreOrder: boolean = thisProduct.seasonUsd <= 0

	const jsonLd = React.useMemo(
		() => [
			buildProductJsonLd({
				name: 'Koinos PLS 8',
				description: getSeoEntry('/shop/pls8').description,
				path: '/shop/pls8',
				image: `${SEO_SITE.origin}/logo.png`,
				product: thisProduct,
				preOrder: isPreOrder,
				sku: 'PLS8',
			}),
			buildBreadcrumbJsonLd([
				{ name: 'Home', path: '/' },
				{ name: 'Shop', path: '/shop' },
				{ name: 'Koinos PLS 8', path: '/shop/pls8' },
			]),
		],
		[thisProduct, isPreOrder],
	)

	const specList: { label: string; value: string }[] = [
		{ label: 'Loops', value: '8 true-bypass loops, plus a buffered insert loop' },
		{ label: 'Loop types', value: 'Loops 1–5 mono · Loops 6–8 stereo-capable' },
		{
			label: 'Stereo modes',
			value: 'Mono, Stereo or Split per stereo loop — up to 13 logical loops',
		},
		{ label: 'Routing', value: 'Crosspoint matrix; chain order stored per preset' },
		{ label: 'Input impedance', value: '500 kΩ or 1 MΩ, selected on screen' },
		{ label: 'Display', value: '8.88-inch 1920 × 480 IPS touch' },
		{
			label: 'Footswitches',
			value: '10 assignable (A–J), each with Tap and Hold; 8 two-switch combinations',
		},
		{ label: 'MIDI out', value: '5-pin DIN · TRS, four polarity modes · 2 × USB host' },
		{ label: 'MIDI in', value: '5-pin DIN' },
		{
			label: 'USB',
			value: '2 × host · 1 × device, for the editor, firmware and USB-MIDI',
		},
		{
			label: 'USB host devices',
			value: 'USB-MIDI devices, or IK Multimedia ToneX One units',
		},
		{
			label: 'MIDI dictionary',
			value: '478 devices and over 21,000 parameters, held on the unit',
		},
		{ label: 'Expression', value: '1 expression input, 1 external switch input' },
		{ label: 'Relay out', value: 'Amp channel switching' },
		{ label: 'Tuner', value: 'Built in — full screen, and a mini tuner while playing' },
		{ label: 'Storage', value: '1,200 songs · 100 setlists' },
		{ label: 'Boot', value: '0.8 seconds' },
		{ label: 'Editor', value: 'Judahs Studio, for macOS and Windows' },
		{ label: 'Power', value: '9 V DC, centre negative' },
	]

	return (
		<View css={style}>
			<Seo jsonLd={jsonLd} />

			<View className="plsPage">
				{/* 00 — Hero */}
				<View className="plsBand plsHero">
					<View className="plsInner">
						<Text as="h1" category="h1" className="plsWordmark">
							Koinos PLS 8
						</Text>
						<Text as="p" category="h4" className="plsTagline">
							Your rig. One screen.
						</Text>
					</View>
				</View>

				{/* 01 — The ritual */}
				<View className="plsBand">
					<View className="plsInner">
						<p className="plsLede">
							Soundcheck is running short. One preset needs one small change. Out comes
							the laptop.
						</p>
						<p className="plsBody">
							We have carried touchscreens in our pockets for twenty years. Switchers
							never noticed.
						</p>
					</View>
				</View>

				{/* 02 — Lineage */}
				<View className="plsBand plsBandRule">
					<View className="plsInner">
						<Text as="p" category="c1" className="plsKicker">
							The part we inherited
						</Text>
						<Text as="h2" category="h2" className="plsLede">
							This audio path stands on older shoulders.
						</Text>
						<p className="plsBody">
							Bob Bradshaw’s Custom Audio Electronics defined professional guitar routing
							in the 1980s. RJM carried that standard forward, and their systems earned
							the trust of players worth trusting. Some of those units are older than the
							musicians touring with them, and still on the road.
						</p>
						<p className="plsBody plsBodyEmphasis">
							We set out to learn that sound. Not to beat it.
						</p>
					</View>
				</View>

				{/* 03 — The turn */}
				<View className="plsBand plsBandRule">
					<View className="plsInner">
						<Text as="p" category="c1" className="plsKicker">
							The part we did not
						</Text>
						<Text as="h2" category="h2" className="plsLede">
							What we chose not to inherit is the place where decisions happen.
						</Text>
						<p className="plsBody">
							An 8.88-inch colour touch display, on the unit itself. At rehearsal, or
							mid-show if it comes to that. No laptop.
						</p>
					</View>
				</View>

				{/* 04 — Three things */}
				<View className="plsBand plsBandRule">
					<View className="plsInner">
						<View className="plsTriad">
							<View className="plsTriadItem">
								<Text as="h3" category="h3" className="plsTriadTitle">
									Reorder your pedals with a touch.
								</Text>
								<p className="plsTriadBody">No cables. Saved per preset.</p>
							</View>
							<View className="plsTriadItem">
								<Text as="h3" category="h3" className="plsTriadTitle">
									Choose what your pickups see.
								</Text>
								<p className="plsTriadBody">500 kΩ or 1 MΩ input, selected on screen.</p>
							</View>
							<View className="plsTriadItem">
								<Text as="h3" category="h3" className="plsTriadTitle">
									Forget CC numbers.
								</Text>
								<p className="plsTriadBody">
									478 devices and over 21,000 MIDI parameters, on the unit.
								</p>
							</View>
						</View>
					</View>
				</View>

				{/* 05 — Built in seasons */}
				<View className="plsBand plsBandRule">
					<View className="plsInner">
						<Text as="h2" category="h2" className="plsLede">
							Built in seasons. A set number, then the season closes.
						</Text>
						<p className="plsBody">
							Each production season is opened, filled, and closed. When it closes, it
							closes.
						</p>
					</View>
				</View>

				{/* 06 — Specifications */}
				<View className="plsBand plsBandRule">
					<View className="plsInner">
						<Text as="h2" category="c1" className="plsKicker">
							Specifications
						</Text>

						<View className="plsSpecTable">
							{specList.map(aSpec => (
								<View key={aSpec.label} className="plsSpecRow">
									<span className="plsSpecLabel">{aSpec.label}</span>
									<span className="plsSpecValue">{aSpec.value}</span>
								</View>
							))}
						</View>
					</View>
				</View>

				{/* 07 — Close */}
				<View className="plsBand plsBandRule plsClose">
					<View className="plsInner">
						<Text as="h2" category="h2" className="plsLede">
							From today it is your pedalboard.
						</Text>
						<p className="plsBody">
							{isPreOrder
								? 'Pricing and the release date have not been set. Write to us and we will tell you when the season opens.'
								: 'The current season is open. See the shop for pricing and availability.'}
						</p>

						<View className="plsActionView">
							<Pressable
								className="plsPrimaryAction"
								onClick={() => navigate(isPreOrder ? '/contact' : '/shop')}
							>
								<span>{isPreOrder ? 'Register your interest' : 'Go to the shop'}</span>
							</Pressable>
							<Pressable className="plsSecondaryAction" onClick={() => navigate('/shop')}>
								<span>See what is shipping now</span>
							</Pressable>
						</View>
					</View>
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default ShopKoinosPls8Page

// 팔레트 — Footer 가 grey[900] 이라 페이지를 어둡게 두면 그대로 이어진다.
const ink = '#0B0C0C'
const inkRaised = '#131514'
const line = '#242926'
const lineSoft = '#191C1A'
const paper = '#EDEAE3'
const paperDim = '#8E8B84'
const paperFaint = '#5C5A55'
const brass = '#A8875C'

const serif = '"Instrument Serif", Georgia, "Times New Roman", serif'

const style = css({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	minHeight: '100%',
	backgroundColor: ink,

	'.plsPage': {
		width: '100%',
	},

	// 밴드 하나가 콘티의 비트 하나. 여백이 이 페이지의 주된 표현 수단이다.
	'.plsBand': {
		display: 'flex',
		justifyContent: 'center',
		width: '100%',
		padding: '7rem 1.5rem',
	},

	'.plsBandRule': {
		borderTop: `1px solid ${lineSoft}`,
	},

	'.plsInner': {
		width: '100%',
		maxWidth: '46rem',
	},

	// Text 컴포넌트는 기본 색이 밝은 배경 기준이라 여기서 덮는다.
	'.text': {
		color: paper,
	},

	/**
	 * Text 컴포넌트가 `.text.h1` 같은 (0,2,0) 셀렉터로 fontSize·fontWeight·fontFamily 를
	 * 박아넣는다. 아래 클래스만 쓰면 (0,1,0) 이라 그쪽이 이겨서 세리프도 크기도 먹지 않는다.
	 * `.plsInner` 를 앞에 붙여 (0,3,0) 으로 올린다.
	 */
	'.plsInner .text.plsWordmark': {
		fontFamily: serif,
		fontSize: 'clamp(3rem, 8vw, 5.5rem)',
		fontWeight: 400,
		lineHeight: 1.02,
	},

	'.plsInner .text.plsTagline': {
		fontSize: '1.1rem',
		fontWeight: 300,
		lineHeight: 1.5,
	},

	'.plsInner .text.plsKicker': {
		fontSize: '0.72rem',
		fontWeight: 500,
		lineHeight: 1.4,
	},

	'.plsInner .text.plsLede': {
		fontFamily: serif,
		fontSize: 'clamp(1.75rem, 3.6vw, 2.75rem)',
		fontWeight: 400,
		lineHeight: 1.22,
	},

	'.plsInner .text.plsTriadTitle': {
		fontFamily: serif,
		fontSize: '1.5rem',
		fontWeight: 400,
		lineHeight: 1.2,
	},

	'.plsHero': {
		padding: '11rem 1.5rem 8rem',
	},

	'.plsWordmark': {
		margin: 0,
		fontFamily: serif,
		fontWeight: 400,
		fontSize: 'clamp(3rem, 8vw, 5.5rem)',
		lineHeight: 1.02,
		letterSpacing: '-0.015em',
		color: paper,
	},

	'.plsTagline': {
		margin: '1.5rem 0 0',
		fontSize: '1.1rem',
		fontWeight: 300,
		letterSpacing: '0.01em',
		color: paperDim,
	},

	'.plsKicker': {
		margin: '0 0 1.5rem',
		fontSize: '0.72rem',
		fontWeight: 500,
		letterSpacing: '0.18em',
		textTransform: 'uppercase',
		color: brass,
	},

	'.plsLede': {
		margin: 0,
		fontFamily: serif,
		fontWeight: 400,
		fontSize: 'clamp(1.75rem, 3.6vw, 2.75rem)',
		lineHeight: 1.22,
		letterSpacing: '-0.01em',
		color: paper,
		textWrap: 'balance',
	},

	'.plsBody': {
		margin: '2rem 0 0',
		fontSize: '1.02rem',
		fontWeight: 300,
		lineHeight: 1.75,
		color: paperDim,
	},

	'.plsBodyEmphasis': {
		color: paper,
	},

	// 04 — 셋으로 묶은 강점. 열 개를 늘어놓으면 자신 없어 보인다.
	'.plsTriad': {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(13rem, 1fr))',
		gap: 0,
		backgroundColor: inkRaised,
	},

	'.plsTriadItem': {
		padding: '2.5rem 2rem',
		borderRight: `1px solid ${lineSoft}`,

		'&:last-of-type': {
			borderRight: 'none',
		},
	},

	'.plsTriadTitle': {
		margin: '0 0 1rem',
		fontFamily: serif,
		fontWeight: 400,
		fontSize: '1.5rem',
		lineHeight: 1.2,
		color: paper,
		textWrap: 'balance',
	},

	'.plsTriadBody': {
		margin: 0,
		fontSize: '0.95rem',
		fontWeight: 300,
		lineHeight: 1.65,
		color: paperDim,
	},

	// 06 — 스펙은 건조하게. 여섯 밴드의 절제 뒤에 오는 무미건조함이 곧 태도다.
	'.plsSpecTable': {
		display: 'flex',
		flexDirection: 'column',
		marginTop: '2.5rem',
	},

	'.plsSpecRow': {
		display: 'grid',
		gridTemplateColumns: '13rem 1fr',
		gap: '1.5rem',
		padding: '0.9rem 0',
		borderTop: `1px solid ${lineSoft}`,

		'&:last-of-type': {
			borderBottom: `1px solid ${lineSoft}`,
		},
	},

	'.plsSpecLabel': {
		fontSize: '0.8rem',
		fontWeight: 400,
		letterSpacing: '0.04em',
		color: paperFaint,
		paddingTop: '0.15rem',
	},

	'.plsSpecValue': {
		fontSize: '0.95rem',
		fontWeight: 300,
		lineHeight: 1.6,
		color: paper,
	},

	'.plsClose': {
		paddingBottom: '9rem',
	},

	'.plsActionView': {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: '1.75rem',
		marginTop: '2.75rem',
	},

	'.plsPrimaryAction': {
		padding: '0.85rem 2.25rem',
		border: `1px solid ${paper}`,
		cursor: 'pointer',
		transition: 'background-color 180ms ease, color 180ms ease',

		span: {
			fontSize: '0.9rem',
			fontWeight: 400,
			letterSpacing: '0.06em',
			textTransform: 'uppercase',
			color: paper,
		},

		'&:hover': {
			backgroundColor: paper,

			span: {
				color: ink,
			},
		},
	},

	'.plsSecondaryAction': {
		cursor: 'pointer',

		span: {
			fontSize: '0.9rem',
			fontWeight: 300,
			color: paperFaint,
			borderBottom: `1px solid ${line}`,
			paddingBottom: '0.15rem',
			transition: 'color 180ms ease, border-color 180ms ease',
		},

		'&:hover span': {
			color: paper,
			borderColor: paperFaint,
		},
	},

	'@media (max-width: 900px)': {
		'.plsBand': {
			padding: '5rem 1.5rem',
		},

		'.plsHero': {
			padding: '7rem 1.5rem 5rem',
		},

		'.plsTriadItem': {
			borderRight: 'none',
			borderBottom: `1px solid ${lineSoft}`,

			'&:last-of-type': {
				borderBottom: 'none',
			},
		},
	},

	'@media (max-width: 600px)': {
		'.plsSpecRow': {
			gridTemplateColumns: '1fr',
			gap: '0.35rem',
		},

		'.plsBody': {
			marginTop: '1.5rem',
		},
	},

	'@media (prefers-reduced-motion: reduce)': {
		'.plsPrimaryAction, .plsSecondaryAction span': {
			transition: 'none',
		},
	},
})
